/**
 * 字段契约（`#field-contract`）：工具返回的每个字段登记一次——是什么、给哪些环节、是否敏感（基线升级第 5 批 M12 下半）。
 * 各环节（事实表、N2、N3、信号、代码）按这里取字段；没登记的字段不会被用到，并在运行留痕里告警（FAC-CONTRACT）。
 * 只读，改契约改 tools/field_contract.json，走 PR。
 * 批 5b（D-13）：敏感字段（卡号后四位、邮箱）标「敏感」、不进模型输入，可只看敏感；每个字段写角色（代码按角色取）、
 * 被哪些运行规则读（点编号去运行规则）、派生哪些数据状态。
 * 收尾第 7 条：收货地址、姓名、付款记录标「个人信息」——照常进模型（改地址、确认地址、退回原支付方式要用），只是标出来；
 * 和「敏感」区分：敏感＝不进模型，个人信息＝进模型但标出来。「标记」可按两者分别筛。
 * 信息流 ③（10-25）：工具返回的字段给了 N3 ／ 信号、N2 却看不到的，运行时告警（FAC-N2-GAP），在顶部第二张卡列出；
 * 确实不用给 N2 的在契约写 n2_skip（原因），N2 列显示「不给」、悬停看原因。
 */
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import {
	type FieldContractRow,
	planErrorMessage,
	usePlanFieldContract,
	usePlanFieldContractAlerts,
} from "@/lib/api/plan-lab";

import { FailState } from "../kit/fail-state";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

const ALL = "__all__";
const CONSUMERS = ["facts", "n2", "n3", "signal", "code"] as const;
const SHORT: Record<string, string> = {
	facts: "事实表",
	n2: "N2",
	n3: "N3",
	signal: "信号",
	code: "代码",
};
/** N2 只进其中一处时的简写（悬停看全称）；列窄，写短 */
const N2_ONLY: Record<string, [string, string]> = {
	notes: ["已知单", "仅已知订单（env.order_notes）"],
	last: ["上步", "仅上步结果（last）"],
};

/** 字段契约 cant_basis 的原因类（G10 按声明认办不了依据，10-27 b34a F6） */
const CANT_BASIS: Record<string, string> = { not_found: "查无", precondition: "前置不满足" };
const SENS = "__sensitive__";
const PII = "__pii__";
const RULED = "__ruled__";

function markOk(r: FieldContractRow, mark: string): boolean {
	if (mark === SENS) return !!r.sensitive;
	if (mark === PII) return !!r.pii;
	if (mark === RULED) return !!r.rules?.length;
	return true;
}

/** 规则编号：点了去「运行规则」页并打开那条 */
function RuleLink({ id }: { id: string }) {
	return (
		<Link
			to="/plan-lab"
			search={{ rule: id }}
			hash="guards"
			title={`去运行规则看 ${id}`}
			className="font-mono text-xs text-text-secondary underline-offset-2 hover:text-brand-accent hover:underline"
			data-testid="plan-field-rule-link"
		>
			{id}
		</Link>
	);
}

const scopeOf = (r: FieldContractRow) =>
	r.scope === "工具" ? "工具" : r.scope === "通用" ? "通用" : "对象";

function Mark({ row, c }: { row: FieldContractRow; c: string }) {
	if (!row.to.includes(c)) {
		if (c === "n2" && row.n2_skip)
			return (
				<span className="text-text-muted" title={`不给 N2：${row.n2_skip}`}>
					不给
				</span>
			);
		return row.object && !row.to.length ? (
			<span className="text-text-muted">按对象</span>
		) : (
			<span className="text-text-muted">—</span>
		);
	}
	const only = c === "n2" && row.n2_only ? N2_ONLY[row.n2_only] : null;
	return (
		<span className="text-text-primary" title={only?.[1]}>
			{only?.[0] ?? "✓"}
		</span>
	);
}

export function FieldContractTab() {
	const q = usePlanFieldContract();
	const alerts = usePlanFieldContractAlerts(3);
	const [scope, setScope] = useState(ALL);
	const [owner, setOwner] = useState(ALL);
	const [consumer, setConsumer] = useState(ALL);
	const [mark, setMark] = useState(ALL);
	const data = q.data;

	const owners = useMemo(
		() => [
			...new Set(
				(data?.rows ?? []).filter((r) => scope === ALL || scopeOf(r) === scope).map((r) => r.key),
			),
		],
		[data, scope],
	);
	const rows = useMemo(
		() =>
			(data?.rows ?? []).filter(
				(r) =>
					(scope === ALL || scopeOf(r) === scope) &&
					(owner === ALL || r.key === owner) &&
					(consumer === ALL || r.to.includes(consumer)) &&
					markOk(r, mark),
			),
		[data, scope, owner, consumer, mark],
	);

	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError || !data)
		return (
			<Card>
				<CardContent>
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId="plan-field-contract-fail"
					/>
				</CardContent>
			</Card>
		);

	// 列宽：上限写在单元格里的文字上（表格单元格的 max-width 不生效，同运行规则页 #611），1440 宽下不横滚
	const columns: PlanColumn<FieldContractRow>[] = [
		{
			key: "field",
			title: "字段",
			render: (r) => (
				<span className="flex w-[150px] flex-wrap items-center gap-1">
					<span className="truncate font-mono" title={r.field}>
						{r.field}
					</span>
					{r.sensitive ? (
						<span
							title="敏感：不给大模型（N2 ／ N3 ／ 信号），投影时剔除"
							data-testid="plan-field-sensitive"
						>
							<Badge tone="danger">敏感</Badge>
						</span>
					) : null}
					{r.pii ? (
						<span
							title="个人信息：照常给模型（改地址、确认地址、退回原支付方式要用），只是标出来，不剔除"
							data-testid="plan-field-pii"
						>
							<Badge tone="info">个人信息</Badge>
						</span>
					) : null}
				</span>
			),
		},
		{
			key: "owner",
			title: "所属",
			render: (r) => {
				const t = r.key !== "*" ? `${r.scope} · ${r.key}` : r.scope;
				return (
					<span className="block w-[120px] truncate text-text-secondary" title={t}>
						{r.scope}
						{r.key !== "*" ? <span className="font-mono"> · {r.key}</span> : null}
					</span>
				);
			},
		},
		{
			key: "desc",
			title: "说明",
			render: (r) => (
				<span
					className="block w-[150px] truncate"
					title={[
						r.desc,
						r.note,
						r.role ? `角色：${r.role}` : "",
						r.cant_basis
							? `可作办不了依据：${CANT_BASIS[r.cant_basis] ?? r.cant_basis}（G10）`
							: "",
					]
						.filter(Boolean)
						.join("；")}
				>
					{r.desc}
					{r.object ? <span className="text-text-muted">（对象：{r.object}）</span> : null}
					{r.role ? <span className="text-text-muted">（角色：{r.role}）</span> : null}
					{r.cant_basis ? (
						<span className="text-text-muted">
							（办不了依据：{CANT_BASIS[r.cant_basis] ?? r.cant_basis}）
						</span>
					) : null}
				</span>
			),
		},
		...CONSUMERS.map(
			(c): PlanColumn<FieldContractRow> => ({
				key: c,
				title: SHORT[c],
				render: (r) => (
					<span className="block w-[44px]">
						<Mark row={r} c={c} />
					</span>
				),
			}),
		),
		{
			key: "rel",
			title: "被哪些规则读 ／ 派生状态",
			render: (r) =>
				r.rules?.length || r.states?.length ? (
					<span className="block w-[170px] space-y-0.5">
						{r.rules?.length ? (
							<span className="flex flex-wrap gap-x-2" title={r.rules.join("、")}>
								{r.rules.slice(0, 3).map((id) => (
									<RuleLink key={id} id={id} />
								))}
								{r.rules.length > 3 ? (
									<span className="text-xs text-text-muted">等 {r.rules.length} 条</span>
								) : null}
							</span>
						) : null}
						{r.states?.length ? (
							<span
								className="block truncate font-mono text-xs text-text-muted"
								title={`派生数据状态：${r.states.join("、")}`}
							>
								→ {r.states.join("、")}
							</span>
						) : null}
					</span>
				) : (
					<span className="text-text-muted">—</span>
				),
		},
	];
	const alertRows = alerts.data?.rows ?? [];
	const hiddenRows = alerts.data?.n2_hidden ?? [];
	const opts = (xs: string[], label: (x: string) => string = (x) => x) => [
		{ value: ALL, label: "全部" },
		...xs.map((x) => ({ value: x, label: label(x) })),
	];

	return (
		<div className="space-y-3">
			{alertRows.length ? (
				<Card data-testid="plan-field-contract-alerts">
					<CardContent className="space-y-2 py-4">
						<div className="text-sm text-text-primary">
							近 {alerts.data?.days} 天有工具返回了没登记的字段（不会被用到，需登记给哪些环节）
						</div>
						<ul className="space-y-1 text-sm">
							{alertRows.map((a) => (
								<li key={`${a.tool}|${a.field}`} className="flex gap-3">
									<span className="font-mono">{a.tool}</span>
									<span className="font-mono">{a.field}</span>
									<span className="text-text-muted">
										{a.count} 次 · 最近 {a.last_seen.slice(0, 16).replace("T", " ")}
									</span>
								</li>
							))}
						</ul>
					</CardContent>
				</Card>
			) : null}
			{hiddenRows.length ? (
				<Card data-testid="plan-field-contract-n2-hidden">
					<CardContent className="space-y-2 py-4">
						<div className="text-sm text-text-primary">
							近 {alerts.data?.days} 天有工具返回的字段给了 N3 ／ 信号、N2 却看不到（FAC-N2-GAP）
						</div>
						<div className="text-xs text-text-muted">
							N2 做决定要用的就登记给 N2；确实不用的在契约里写 n2_skip（原因），不再告警
						</div>
						<ul className="space-y-1 text-sm">
							{hiddenRows.map((a) => (
								<li key={`${a.tool}|${a.field}`} className="flex gap-3">
									<span className="font-mono">{a.tool}</span>
									<span className="font-mono">{a.field}</span>
									<span className="text-text-muted">
										给 {(a.to ?? []).map((c) => SHORT[c] ?? c).join("、") || "—"} · {a.count} 次 ·
										最近 {a.last_seen.slice(0, 16).replace("T", " ")}
									</span>
								</li>
							))}
						</ul>
					</CardContent>
				</Card>
			) : null}
			<Card data-testid="plan-field-contract">
				<CardContent className="space-y-3 py-4">
					<div className="flex flex-wrap items-center gap-2">
						<LabeledSelect
							label="范围"
							value={scope}
							onChange={(v) => {
								setScope(v);
								setOwner(ALL);
							}}
							options={opts(["对象", "通用", "工具"])}
						/>
						<LabeledSelect label="所属" value={owner} onChange={setOwner} options={opts(owners)} />
						<LabeledSelect
							label="给哪个环节"
							value={consumer}
							onChange={setConsumer}
							options={opts([...CONSUMERS], (c) => data.consumers[c] ?? c)}
						/>
						<LabeledSelect
							label="标记"
							value={mark}
							onChange={setMark}
							options={[
								{ value: ALL, label: "全部" },
								{ value: SENS, label: "只看敏感（不进模型）" },
								{ value: PII, label: "只看个人信息（进模型）" },
								{ value: RULED, label: "有规则在读" },
							]}
						/>
						<span className="ml-auto text-xs text-text-muted">
							{alerts.isSuccess && !alertRows.length ? "近 3 天没有未登记字段 · " : ""}共{" "}
							{rows.length} 个字段
						</span>
					</div>
					{rows.length ? (
						<PlanTable
							columns={columns}
							rows={rows}
							rowKey={(r) => `${r.scope}|${r.key}|${r.field}`}
							density="compact"
							rowTestId="plan-field-contract-row"
						/>
					) : (
						<EmptyState title="没有符合条件的字段" desc="换个范围或环节看看。" />
					)}
				</CardContent>
			</Card>
		</div>
	);
}
